import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ScenarioTypeEnum } from '@app/shared/enums';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { Municipality, MunicipalityService } from '@app/shared/services/api/municipality.service';
import { ScenarioPlannerService } from '@app/shared/services/api/scenario-planner.service';
import { AuthService } from '@app/shared/services/auth.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { environment } from '@env/environment';
import { filter, flatten, groupBy, isNil, orderBy, toLower, trim } from 'lodash';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-product-catalog',
  templateUrl: './product-catalog.component.html',
  styleUrls: ['./product-catalog.component.scss'],
})
export class ProductCatalogComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  selectedLamp: any | null = null;
  isProductLoading: boolean = false;
  isKamUser: boolean = false;
  catalog: any[] = [];
  rawData: any[] = [];
  searchTerm?: string;
  recommended: any[] = [];
  nonrecommended: any[] = [];
  @Input() selectedLampIds: any[] = [];
  @Input() scenarioType!: string;
  @Output() showCatalog = new EventEmitter();
  @Output() selectLamp = new EventEmitter();
  filteredOptions: Observable<string[]> = of();
  public searchControl = new FormControl();

  constructor(
    public authService: AuthService,
    private azureBlobStorageService: AzureBlobStorageService,
    private municipalityService: MunicipalityService,
    private scenarioPlannerService: ScenarioPlannerService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {}

  public ngOnInit(): void {
    this.isKamUser = this.authService.isKAM;
    this.getAlternatives();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getAlternatives() {
    this.isProductLoading = true;

    this.municipalityService
      .getByMunicipalityKey(this.userMunicipalitiesService.currentMunicipality.key)
      .pipe(
        switchMap((municipalites: Municipality[]) => {
          const getObservables = this.selectedLampIds.map((id) => {
            return this.scenarioPlannerService.getLampCatalogue(
              id,
              this.userMunicipalitiesService.currentMunicipality.key,
              municipalites[0].grossElectricityPricePerUnit as number,
              municipalites[0].co2Emission as number
            );
          });

          return forkJoin(getObservables);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((aggregatedData: any[]) => {
        const flatData = flatten(aggregatedData);
        const groupedData = groupBy(flatData, 'productId');
        const dataIntersect = filter(groupedData, (a) => a.length === this.selectedLampIds.length);
        const data = flatten(dataIntersect);

        this.rawData = data;

        this.catalog = data
          .map((lamp) => {
            return {
              active: false,
              image: this.azureBlobStorageService.getBlobImageSafeUrl(`${environment.tenant}/assets/images/lamps/${lamp.image}`),
              uvpCost: lamp.uvpCost,
              salesPrice: lamp.salesPrice,
              count: 0,
              suggestedManufacturer: lamp.suggestedManufacturer,
              suggestedModel: lamp.suggestedModel,
              energySavings: lamp.annualEnergyConsumption,
              suggestedCharacterNo: lamp.suggestedCharacterNo,
              suggestedWatts: lamp.suggestedWatts,
              suggestedGuaranteeYears: lamp.suggestedGuaranteeYears,
              costSavings: lamp.costSavings,
              cO2Savings: lamp.cO2Savings,
              suggestedLuminousFlux4000K: lamp.suggestedLuminousFlux4000K,
              suggestedLuminousFlux3000K: lamp.suggestedLuminousFlux3000K,
              productId: lamp.productId,
              lightType: lamp.lightType,
              preferredLuminosity: lamp.preferredLuminosity,
              suggestedMinLumen: lamp.suggestedMinLumen,
              suggestedMaxLumen: lamp.suggestedMaxLumen,
              kamAmortisationTime: lamp.kamAmortisationTime,
            };
          })
          .reduce((x: any[], y) => (x.findIndex((e) => e.productId === y.productId) < 0 ? [...x, y] : x), []);

        this.searchCatalog('');

        this.filteredOptions = this.searchControl.valueChanges.pipe(
          startWith(''),
          map((value) => this._filter(value))
        );

        this.isProductLoading = false;
      });
  }

  selectModel(lamp: any) {
    this.selectedLamp = lamp;
  }

  save() {
    if (!isNil(this.selectedLamp)) {
      const suggestion = this.rawData.filter((product) => product.productId === this.selectedLamp.productId);
      this.selectLamp.emit(suggestion);
      this.showCatalog.emit(false);
    }
  }

  close() {
    this.showCatalog.emit(false);
  }

  searchCatalog(searchTerm: string) {
    this.searchTerm = searchTerm;

    if (isNil(searchTerm) || trim(searchTerm).length <= 0) {
      this.recommended = this.catalog.filter((x) => x.suggestedLuminousFlux4000K >= x.suggestedMinLumen && x.suggestedLuminousFlux4000K <= x.suggestedMaxLumen);
      if (this.authService.permissions.STREETLIGHT_PLANNER_LED_RETROFIT) {
        this.nonrecommended = this.catalog.filter(
          (x) => x.suggestedLuminousFlux4000K < x.suggestedMinLumen || x.suggestedLuminousFlux4000K > x.suggestedMaxLumen
        );
      }
    } else {
      this.recommended = this.catalog.filter(
        (x) =>
          x.suggestedLuminousFlux4000K >= x.suggestedMinLumen &&
          x.suggestedLuminousFlux4000K <= x.suggestedMaxLumen &&
          toLower(x.suggestedModel).includes(searchTerm)
      );
      if (this.authService.permissions.STREETLIGHT_PLANNER_LED_RETROFIT) {
        this.nonrecommended = this.catalog.filter(
          (x) =>
            (x.suggestedLuminousFlux4000K < x.suggestedMinLumen || x.suggestedLuminousFlux4000K > x.suggestedMaxLumen) &&
            toLower(x.suggestedModel).includes(searchTerm)
        );
      }
    }

    if (toLower(this.scenarioType) === toLower(ScenarioTypeEnum.economical)) {
      const orderColumn = this.isKamUser ? 'kamAmortisationTime' : 'kamortisationTime';
      this.recommended = orderBy(this.recommended, [orderColumn, 'suggestedLuminousFlux4000K'], ['asc', 'desc']);
      this.nonrecommended = orderBy(this.nonrecommended, [orderColumn, 'suggestedLuminousFlux4000K'], ['asc', 'desc']);
    } else {
      this.recommended = orderBy(this.recommended, ['energySavings', 'suggestedLuminousFlux4000K'], ['desc', 'desc']);
      this.nonrecommended = orderBy(this.nonrecommended, ['energySavings', 'suggestedLuminousFlux4000K'], ['desc', 'desc']);
    }
  }

  private _filter(value: any): string[] {
    const filterValue = value ? (value.suggestedModel ? toLower(value.suggestedModel) : toLower(value)) : '';
    this.searchCatalog(filterValue);
    return this.catalog.filter((option) => toLower(option.suggestedModel).includes(filterValue));
  }

  displayFormat(data: any): string | undefined {
    return data ? data.suggestedModel : undefined;
  }

  trackByFnOption(_: number, item: any) {
    return item.productId;
  }

  trackByFnRecommended(_: number, item: any) {
    return item.productId;
  }

  trackByFnNonRecommended(_: number, item: any) {
    return item.productId;
  }
}
